import { EmblaCarouselType } from 'embla-carousel';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { UiKitCarouselBase } from '@/fo/ui/kit/carousel/base';
import { UiKitRatingBase } from '@/fo/ui/kit/rating/base';
import { Review } from '@/fo/ui/review/catalog/base';
import { ReviewImage } from '@/fo/ui/review/gallery/base';
import { utilPluralize } from '@/fo/util/pluralize';

import { CardWrapper } from '../CardWrapper/CardWrapper';
import { ContentBlock } from '../ContentBlock/ContentBlock';

import { styles } from './ReviewCard.styles';

export interface ReviewCardProps {
  review: Review;
  className?: string;
  onEmblaApiChange?: (emblaApi: EmblaCarouselType) => void;
  onClick?: () => void;
  onImageClick?: (imageId: string, images: ReviewImage[]) => void;
}

export function ReviewCard({
  review,
  className,
  onClick,
  onImageClick
}: ReviewCardProps) {
  const t = useTranslations();
  const slots = styles();

  const pluralizeAge = utilPluralize({
    one: t('pluralize.age.one'),
    few: t('pluralize.age.few'),
    many: t('pluralize.age.many')
  });

  const ageText = review.author.age
    ? `${review.author.age} ${pluralizeAge(review.author.age)}`
    : null;

  const images = review.images.length > 0 && (
    <UiKitCarouselBase
      showArrows={false}
      loop={false}
      className={slots.carousel()}
      isNested={true}
      dragFree={true}
    >
      {review.images.map((img) => (
        <button
          key={img.id}
          type="button"
          onClick={() => onImageClick?.(img.id, review.images)}
        >
          <Image
            src={img.url}
            alt={img.alt}
            width={100}
            height={100}
            className={slots.image()}
          />
        </button>
      ))}
    </UiKitCarouselBase>
  );

  const header = (
    <div className={slots.header()}>
      <div className={slots.headerFirstBlock()}>
        <UiKitRatingBase value={review.rating} />
        {<span className={slots.date()}>{review.date}</span>}
      </div>
      <div className={slots.headerSecondBlock()}>
        <div>
          <p className={slots.authorName()}>{review.author.name}</p>
        </div>
        <span className={slots.authorMeta()}>
          {ageText}
          {review.author.skinType && ageText && ' / '}
          {review.author.skinType}
          {review.author.skinType && ` ${t('skin')}`}
        </span>
      </div>
      <div>{images}</div>
    </div>
  );

  const content = (
    <div className={slots.content()}>
      {review.pros && (
        <ContentBlock
          title={t('review.labels.pros')}
          text={review.pros}
        />
      )}
      {review.cons && (
        <ContentBlock
          title={t('review.labels.cons')}
          text={review.cons}
        />
      )}
      {review.comment && (
        <ContentBlock
          title={t('review.labels.comment')}
          text={review.comment}
        />
      )}
    </div>
  );

  return (
    <CardWrapper
      className={className}
      header={header}
      content={content}
      actionButtonText={t('review.buttons.readFullReview')}
      onActionButtonClick={onClick}
    />
  );
}
